import React, { useState, useEffect, useReducer, useCallback, useContext, useRef } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";
import AddIcon from "@material-ui/icons/Add";
import EventNoteIcon from "@material-ui/icons/EventNote";
import TodayIcon from "@material-ui/icons/Today";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AgendaDashboard from "../../components/AgendaDashboard";
import AppointmentModal from "../../components/AppointmentModal";
import ServicesManager from "../../components/ServicesManager";
import LocationsManager from "../../components/LocationsManager";
import AvailabilityManager from "../../components/AvailabilityManager";
import ScheduleBlocksManager from "../../components/ScheduleBlocksManager";
import { KoraxPage, KoraxPageHeader } from "../../components/KoraxUI";

moment.locale("pt-br");
const localizer = momentLocalizer(moment);

const defaultMessages = {
  date: "Data",
  time: "Hora",
  event: "Agendamento",
  allDay: "Dia Todo",
  week: "Semana",
  day: "Dia",
  month: "Mês",
  previous: "Anterior",
  next: "Próximo",
  today: "Hoje",
  agenda: "Agenda",
  noEventsInRange: "Não há agendamentos neste período.",
  showMore: total => `+${total} mais`
};

const statusColors = {
  agendado: "#0066FF",
  confirmado: "#00A3FF",
  em_atendimento: "#F59E0B",
  finalizado: "#22C55E",
  cancelado: "#EF4444",
  nao_compareceu: "#94A3B8"
};

const professionalColors = [
  "#7C3AED",
  "#059669",
  "#EA580C",
  "#DB2777",
  "#0891B2",
  "#4F46E5",
  "#65A30D",
  "#D97706"
];

const statusLabels = {
  agendado: "Agendado",
  confirmado: "Confirmado",
  em_atendimento: "Em atendimento",
  finalizado: "Finalizado",
  cancelado: "Cancelado",
  nao_compareceu: "Não compareceu"
};

const getProfessionalColor = appointment => {
  const professional =
    appointment?.professional ||
    appointment?.user;

  const seed = `${appointment?.professionalId || ""}${
    professional?.name || ""
  }`;

  let hash = 0;

  for (let i = 0; i < seed.length; i += 1) {
    hash =
      (hash * 31 + seed.charCodeAt(i)) %
      2147483647;
  }

  return professionalColors[
    Math.abs(hash) %
      professionalColors.length
  ];
};


const reducer = (state, action) => {
  if (action.type === "LOAD_APPOINTMENTS") return Array.isArray(action.payload) ? action.payload : [];
  if (action.type === "UPDATE_APPOINTMENT") {
    const appointment = action.payload;
    const index = state.findIndex(a => a.id === appointment.id);
    if (index !== -1) {
      const next = [...state];
      next[index] = appointment;
      return next;
    }
    return [appointment, ...state];
  }
  if (action.type === "DELETE_APPOINTMENT") return state.filter(a => a.id !== action.payload);
  if (action.type === "RESET") return [];
  return state;
};

const useStyles = makeStyles(theme => ({
  pageRoot: {
    minHeight: "calc(100vh - 56px)",
    paddingBottom: theme.spacing(8),
    gap: theme.spacing(1.5),
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    background: "linear-gradient(145deg,#EEF5FF 0%,#F8FAFC 100%)",

    [theme.breakpoints.down("sm")]: {
      paddingBottom: theme.spacing(9)
    }
  },
  hero: {
    borderRadius: 28,
    padding: theme.spacing(2.3, 2.6),
    background: "linear-gradient(135deg,#FFFFFF 0%,#EAF3FF 55%,#F8FAFC 100%)",
    border: "1px solid #E2E8F0",
    boxShadow: "0 22px 55px rgba(0,102,255,.11)",
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      borderRadius: 22,
      padding: theme.spacing(1.6)
    }
  },
  heroLeft: { display: "flex", alignItems: "center", gap: theme.spacing(1.4), minWidth: 0 },
  heroIcon: {
    width: 58,
    height: 58,
    borderRadius: 20,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 27,
    color: "#fff",
    boxShadow: "0 10px 24px rgba(0,102,255,.28)",
    flexShrink: 0
  },
  heroTitle: { fontWeight: 950, fontSize: "1.55rem", color: "#0F172A", lineHeight: 1.05 },
  heroSubtitle: { fontSize: ".82rem", color: "#64748B", marginTop: 6, lineHeight: 1.45 },
  heroRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& button": { width: "100%" }
    }
  },
  heroChip: {
    borderRadius: 999,
    background: "#FFFFFF",
    border: "1px solid rgba(0,102,255,.14)",
    color: "#0F172A",
    fontWeight: 900,
    fontSize: ".72rem"
  },
  addBtn: {
    borderRadius: 16,
    fontWeight: 900,
    textTransform: "none",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    padding: theme.spacing(1, 2),
    boxShadow: "0 12px 26px rgba(0,102,255,.22)",
    "&:hover": { background: "linear-gradient(135deg,#0052CC,#007ACC)" }
  },
  tabsWrap: {
    borderRadius: 20,
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    boxShadow: "0 10px 30px rgba(15,23,42,.05)",
    padding: 6
  },

  navInner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,

    [theme.breakpoints.down("sm")]: {
      alignItems: "stretch",
      flexDirection: "column"
    },

    "& .MuiTabs-root": {
      flex: 1,
      minHeight: 46,
      minWidth: 0
    },

    "& .MuiTabs-scroller": {
      overflowX: "auto !important"
    },

    "& .MuiTabs-flexContainer": {
      minWidth: 690,
      gap: 4
    },

    "& .MuiTabs-indicator": {
      display: "none"
    },

    "& .MuiTab-root": {
      minHeight: 44,
      minWidth: 104,
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 850,
      color: "#64748B",
      transition: "all .18s ease"
    },

    "& .MuiTab-root.Mui-selected": {
      color: "#0057FF",
      background: "#EEF5FF",
      boxShadow: "inset 0 0 0 1px rgba(0,87,255,.10)"
    }
  },

  navNewButton: {
    flexShrink: 0,
    borderRadius: 13,
    padding: theme.spacing(1, 1.8),
    fontWeight: 900,
    textTransform: "none",
    whiteSpace: "nowrap",
    background: "linear-gradient(135deg,#0057FF,#008CFF)",
    color: "#FFFFFF",
    boxShadow: "0 9px 20px rgba(0,87,255,.20)",

    "&:hover": {
      background: "linear-gradient(135deg,#004AD9,#0075D9)"
    },

    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },

  agendaPulse: {
    display: "grid",
    gridTemplateColumns: "repeat(4,minmax(0,1fr))",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(.2),

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2,minmax(0,1fr))"
    }
  },

  pulseCard: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 16,
    padding: theme.spacing(1.25, 1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    boxShadow: "0 7px 22px rgba(15,23,42,.035)"
  },

  pulseIcon: {
    width: 34,
    height: 34,
    borderRadius: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#EEF5FF",
    color: "#0057FF",
    flexShrink: 0
  },

  pulseValue: {
    fontSize: "1.08rem",
    lineHeight: 1.1,
    fontWeight: 950,
    color: "#0F172A"
  },

  pulseLabel: {
    marginTop: 2,
    fontSize: ".69rem",
    fontWeight: 750,
    color: "#64748B"
  },
  agendaLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 390px",
    gap: theme.spacing(1.5),
    alignItems: "start",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" }
  },
  calendarCard: {
    borderRadius: 28,
    overflow: "hidden",
    background: "#fff",
    border: "1px solid #E2E8F0",
    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
    padding: theme.spacing(1.2)
  },
  calendarWrap: {
    minHeight: 650,
    "& .rbc-calendar": { fontFamily: "Inter, sans-serif" },
    "& .rbc-toolbar": { display: "none" },
    "& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view": {
      borderRadius: 22,
      overflow: "hidden",
      border: "1px solid #E2E8F0"
    },
    "& .rbc-header": {
      padding: "13px 4px",
      color: "#64748B",
      fontSize: ".72rem",
      fontWeight: 950,
      textTransform: "uppercase",
      background: "#F8FAFC",
      borderColor: "#E2E8F0"
    },
    "& .rbc-date-cell": { padding: 10, fontWeight: 800, color: "#1E293B", cursor: "pointer" },
    "& .rbc-off-range": { color: "#94A3B8" },
    "& .rbc-today": { background: "rgba(0,102,255,.05)" },
    "& .korax-selected-day": {
      background: "rgba(0,102,255,.10) !important",
      boxShadow: "inset 0 0 0 2px rgba(0,102,255,.25)"
    },
    "& .rbc-event": {
      borderRadius: 999,
      padding: "4px 8px",
      fontWeight: 900,
      fontSize: 11,
      border: "none"
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 480,
      "& .rbc-month-row": { minHeight: 78 },
      "& .rbc-event": { fontSize: 10, padding: "2px 5px" },
      "& .rbc-date-cell": { padding: 6 }
    }
  },
  calendarFilters: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.1),
    padding: theme.spacing(1),
    background: "#F8FAFC",
    border: "1px solid #E2E8F0",
    borderRadius: 16
  },

  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(.8),
    flexWrap: "wrap",

    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },

  filterSelect: {
    height: 38,
    minWidth: 155,
    borderRadius: 11,
    border: "1px solid #DCE3EC",
    background: "#FFFFFF",
    padding: "0 34px 0 11px",
    color: "#334155",
    fontSize: ".76rem",
    fontWeight: 800,
    outline: "none",
    cursor: "pointer",

    "&:focus": {
      borderColor: "#0057FF",
      boxShadow: "0 0 0 3px rgba(0,87,255,.08)"
    },

    [theme.breakpoints.down("sm")]: {
      flex: "1 1 145px",
      minWidth: 0
    }
  },

  filterClear: {
    height: 38,
    borderRadius: 11,
    border: "1px solid #DCE3EC",
    background: "#FFFFFF",
    padding: "0 12px",
    color: "#475569",
    fontSize: ".74rem",
    fontWeight: 850,
    cursor: "pointer"
  },

  filterCount: {
    fontSize: ".72rem",
    fontWeight: 850,
    color: "#64748B",
    whiteSpace: "nowrap"
  },

  calendarLegend: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    padding: theme.spacing(.4, .4, 1.2)
  },

  legendSide: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    flexWrap: "wrap"
  },

  legendTitle: {
    fontSize: ".68rem",
    fontWeight: 900,
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: ".04em",
    marginRight: 3
  },

  professionalLegendChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 28,
    padding: "0 9px",
    borderRadius: 999,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    fontSize: ".7rem",
    fontWeight: 850,
    color: "#334155"
  },

  legendDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0
  },

  calendarEvent: {
    width: "100%",
    minWidth: 0,
    padding: "5px 6px 4px"
  },

  eventTop: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    gap: 5
  },

  eventTime: {
    fontSize: 10,
    fontWeight: 950,
    color: "#0F172A",
    flexShrink: 0
  },

  eventClient: {
    fontSize: 10.5,
    fontWeight: 900,
    color: "#0F172A",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1
  },

  eventStatusDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    flexShrink: 0
  },

  eventMeta: {
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 4,
    minWidth: 0,
    color: "#64748B",
    fontSize: 9,
    fontWeight: 750,
    whiteSpace: "nowrap",
    overflow: "hidden"
  },

  eventMetaText: {
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  customToolbar: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: theme.spacing(1.2),
    marginBottom: theme.spacing(1.4),
    padding: theme.spacing(1.1),
    borderRadius: 18,
    background: "linear-gradient(135deg,#F8FAFC,#EEF5FF)",
    border: "1px solid #E2E8F0",

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      padding: theme.spacing(1)
    }
  },

  toolbarLeft: {
    display: "flex",
    gap: theme.spacing(.7),
    alignItems: "center",
    flexWrap: "nowrap",

    [theme.breakpoints.down("sm")]: {
      justifyContent: "space-between"
    }
  },

  toolbarCenter: {
    textAlign: "center",
    fontWeight: 950,
    fontSize: "1.05rem",
    color: "#0F172A",
    textTransform: "capitalize",
    whiteSpace: "nowrap"
  },

  toolbarViews: {
    display: "flex",
    justifyContent: "flex-end",
    background: "#FFFFFF",
    borderRadius: 12,
    padding: 4,
    gap: 3,
    border: "1px solid #E2E8F0",

    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      width: "100%",

      "& button": {
        flex: 1
      }
    }
  },

  toolbarTodayBtn: {
    minHeight: 38,
    border: "1px solid #D7E3F4",
    background: "#FFFFFF",
    borderRadius: 11,
    padding: "7px 12px",
    cursor: "pointer",
    fontWeight: 850,
    color: "#1E3A5F",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "all .18s ease",

    "&:hover": {
      borderColor: "#0066FF",
      color: "#0066FF",
      background: "#F7FAFF"
    }
  },

  toolbarIconBtn: {
    width: 38,
    height: 38,
    border: "1px solid #D7E3F4",
    background: "#FFFFFF",
    borderRadius: 11,
    cursor: "pointer",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all .18s ease",

    "&:hover": {
      borderColor: "#0066FF",
      color: "#0066FF",
      background: "#F7FAFF"
    },

    "& svg": {
      fontSize: 20
    }
  },

  toolbarAddBtn: {
    minHeight: 38,
    border: 0,
    borderRadius: 11,
    padding: "7px 13px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: "#FFFFFF",
    fontWeight: 850,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    boxShadow: "0 8px 18px rgba(0,102,255,.20)",

    "& svg": {
      fontSize: 18
    },

    "&:hover": {
      background: "linear-gradient(135deg,#0052CC,#0088DD)"
    }
  },

  viewBtn: {
    minHeight: 34,
    border: "none",
    background: "transparent",
    borderRadius: 9,
    padding: "7px 11px",
    cursor: "pointer",
    fontWeight: 850,
    fontSize: ".74rem",
    color: "#64748B",
    transition: "all .18s ease"
  },

  viewBtnActive: {
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#FFFFFF",
    boxShadow: "0 6px 14px rgba(0,102,255,.20)"
  },
  dayPanel: {
    borderRadius: 28,
    background: "#fff",
    border: "1px solid #E2E8F0",
    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
    overflow: "hidden",
    position: "sticky",
    top: 12,
    [theme.breakpoints.down("md")]: { position: "relative", top: "auto" }
  },
  dayPanelTop: {
    padding: theme.spacing(1.8),
    color: "#fff",
    background: "linear-gradient(135deg,#0B2A7A 0%,#0066FF 62%,#00A3FF 100%)",
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  dayPanelTitle: { fontWeight: 950, fontSize: "1.05rem", color: "#fff" },
  dayPanelSubtitle: { fontSize: ".78rem", opacity: .86, marginTop: 5 },
  dayIcon: {
    width: 50,
    height: 50,
    borderRadius: 16,
    background: "rgba(255,255,255,.16)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "& svg": {
      fontSize: 24,
      color: "#FFFFFF"
    }
  },
  dayContent: { padding: theme.spacing(1.4) },
  dayStatsGrid: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: theme.spacing(.8), marginBottom: theme.spacing(1.2) },
  dayStat: {
    borderRadius: 18,
    padding: theme.spacing(1),
    background: "#F8FAFC",
    border: "1px solid #E2E8F0",
    textAlign: "center"
  },
  dayStatValue: { fontWeight: 950, fontSize: "1.05rem", color: "#0F172A" },
  dayStatLabel: { fontSize: ".64rem", fontWeight: 850, color: "#64748B", marginTop: 2 },
  slotsList: {
    display: "grid",
    gap: theme.spacing(.8),
    maxHeight: 515,
    overflowY: "auto",
    paddingRight: 3,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: { maxHeight: "none", overflowY: "visible" }
  },
  slotRow: {
    borderRadius: 18,
    padding: theme.spacing(1),
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    display: "grid",
    gridTemplateColumns: "56px 1fr auto",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
    transition: "all .15s",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 22px rgba(15,23,42,.08)",
      borderColor: "rgba(0,102,255,.28)"
    },
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "52px 1fr auto" }
  },
  slotClock: {
    width: 40,
    height: 40,
    borderRadius: 14,
    background: "rgba(0,102,255,.08)",
    color: "#0066FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950
  },
  slotTime: { fontWeight: 950, color: "#0F172A", fontSize: ".88rem" },
  slotInfo: {
    color: "#64748B",
    fontSize: ".7rem",
    marginTop: 2,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: 190
  },
  slotPill: { borderRadius: 999, padding: "6px 10px", fontSize: ".68rem", fontWeight: 950, whiteSpace: "nowrap" },
  embeddedManagersGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    }
  },
  embeddedManagerCard: {
    borderRadius: 24,
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    boxShadow: "0 14px 34px rgba(15,23,42,.06)",
    overflow: "hidden"
  },
  embeddedManagerHeader: {
    padding: theme.spacing(1.4, 1.6),
    borderBottom: "1px solid #E2E8F0",
    background: "linear-gradient(135deg,#F8FAFC,#EEF5FF)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  embeddedManagerTitle: {
    fontWeight: 950,
    fontSize: ".94rem",
    color: "#0F172A"
  },
  embeddedManagerSubtitle: {
    fontSize: ".7rem",
    color: "#64748B",
    marginTop: 3
  },
  embeddedManagerBody: {
    padding: theme.spacing(1),
    maxHeight: 460,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      maxHeight: "none",
      overflowY: "visible"
    }
  },

  aiCard: {
    marginTop: theme.spacing(1.2),
    borderRadius: 20,
    padding: theme.spacing(1.2),
    background: "linear-gradient(135deg,#EEF5FF,#FFFFFF)",
    border: "1px solid rgba(0,102,255,.14)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  aiTitle: { fontSize: ".78rem", fontWeight: 950, color: "#0F172A" },
  aiText: { fontSize: ".68rem", color: "#64748B", marginTop: 3 },
  mobileSpacer: { height: 90 }
}));

const Agenda = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [tab, setTab] = useState(1);
  const [appointments, dispatch] = useReducer(reducer, []);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("month");

  const [locationFilter, setLocationFilter] = useState("");
  const [professionalFilter, setProfessionalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const dayPanelRef = useRef(null);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function checkPlan() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useAgenda) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => history.push(`/`), 1000);
      }
    }
    checkPlan();
  }, [user, history, getPlanCompany]);

  const fetchAppointments = useCallback(async () => {
    try {
      const { data } = await api.get("/appointments");
      dispatch({ type: "LOAD_APPOINTMENTS", payload: data });
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  useEffect(() => {
    const onCompanyAppointment = data => {
      if (data.action === "update" || data.action === "create") dispatch({ type: "UPDATE_APPOINTMENT", payload: data.appointment });
      if (data.action === "delete") dispatch({ type: "DELETE_APPOINTMENT", payload: +data.id });
    };

    socket.on(`company${user.companyId}-appointment`, onCompanyAppointment);
    return () => socket.off(`company${user.companyId}-appointment`, onCompanyAppointment);
  }, [socket, user.companyId]);

  const openNewAppointment = slot => {
    setSelectedAppointment(null);
    setSelectedSlot(slot || null);
    setAppointmentModalOpen(true);
  };

  const handleOpenAppointmentModal = () => openNewAppointment(null);

  const handleSelectSlot = slotInfo => {
    const date = slotInfo.start || new Date();
    setSelectedDate(date);
    setCalendarDate(date);

    if (calendarView === "month") {
      setTimeout(() => {
        if (dayPanelRef.current && window.innerWidth <= 960) {
          dayPanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 120);
      return;
    }

    openNewAppointment(slotInfo);
  };

  const handleSelectEvent = event => {
    setSelectedDate(event.start || new Date());
    setSelectedSlot(null);
    setSelectedAppointment(event.resource);
    setAppointmentModalOpen(true);
  };

  const handleCloseAppointmentModal = () => {
    setSelectedAppointment(null);
    setSelectedSlot(null);
    setAppointmentModalOpen(false);
  };

  const filteredAppointments =
    appointments.filter(appointment => {
      const locationId =
        appointment.locationId ||
        appointment.location?.id;

      const professionalId =
        appointment.professionalId ||
        appointment.professional?.id ||
        appointment.user?.id;

      if (
        locationFilter &&
        String(locationId) !==
          String(locationFilter)
      ) {
        return false;
      }

      if (
        professionalFilter &&
        String(professionalId) !==
          String(professionalFilter)
      ) {
        return false;
      }

      if (
        statusFilter &&
        appointment.status !==
          statusFilter
      ) {
        return false;
      }

      return true;
    });


  const calendarLocations =
    Array.from(
      new Map(
        appointments
          .filter(
            appointment =>
              appointment.location?.id ||
              appointment.locationId
          )
          .map(appointment => [
            String(
              appointment.location?.id ||
              appointment.locationId
            ),
            {
              id:
                appointment.location?.id ||
                appointment.locationId,

              name:
                appointment.location?.name ||
                `Unidade ${
                  appointment.locationId
                }`
            }
          ])
      ).values()
    );


  const calendarProfessionals =
    Array.from(
      new Map(
        appointments
          .filter(
            appointment =>
              appointment.professional?.id ||
              appointment.professionalId ||
              appointment.user?.id
          )
          .map(appointment => {
            const professional =
              appointment.professional ||
              appointment.user;

            const id =
              professional?.id ||
              appointment.professionalId;

            return [
              String(id),
              {
                id,
                name:
                  professional?.name ||
                  `Profissional ${id}`
              }
            ];
          })
      ).values()
    );


  const events = filteredAppointments.map(appointment => ({
    title: `${moment(appointment.startTime).format("HH:mm")} ${appointment.contact?.name || "Cliente"}`,
    start: new Date(appointment.startTime),
    end: new Date(appointment.endTime),
    resource: appointment
  }));

  const selectedDayAppointments = filteredAppointments
    .filter(a => moment(a.startTime).isSame(selectedDate, "day"))
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

  const quickSlots = Array.from({ length: 10 }).map((_, index) => {
    const hour = 8 + index;
    const start = moment(selectedDate).hour(hour).minute(0).second(0).millisecond(0).toDate();
    const end = moment(start).add(60, "minutes").toDate();
    const appointment = selectedDayAppointments.find(a => moment(a.startTime).hour() === hour);
    return { hour, start, end, appointment };
  });

  const dayCapacity = quickSlots.length;
  const dayActive = selectedDayAppointments.filter(a => !["finalizado", "cancelado", "nao_compareceu"].includes(a.status)).length;
  const dayOccupancy = Math.min(Math.round((dayActive / dayCapacity) * 100), 100);
  const freeSlots = Math.max(dayCapacity - dayActive, 0);

  const getDayStatus = () => {
    if (dayOccupancy >= 80) return { label: "Quase cheia", bg: "rgba(245,158,11,.12)", color: "#B45309", border: "1px solid rgba(245,158,11,.25)" };
    if (dayOccupancy >= 35) return { label: "Movimentada", bg: "rgba(0,102,255,.10)", color: "#0066FF", border: "1px solid rgba(0,102,255,.22)" };
    return { label: "Disponível", bg: "rgba(34,197,94,.12)", color: "#16A34A", border: "1px solid rgba(34,197,94,.25)" };
  };

  const dayStatus = getDayStatus();

  const eventStyleGetter = event => {
    const appointment =
      event.resource || {};

    const professionalColor =
      getProfessionalColor(appointment);

    return {
      style: {
        backgroundColor: "#FFFFFF",
        borderRadius: 9,
        color: "#0F172A",
        border: "1px solid #E2E8F0",
        borderLeft:
          `4px solid ${professionalColor}`,
        boxShadow:
          "0 3px 10px rgba(15,23,42,.08)",
        padding: 0,
        overflow: "hidden",
        opacity:
          appointment.status === "cancelado"
            ? 0.58
            : 1
      }
    };
  };

  const CalendarEvent = ({ event }) => {
    const appointment =
      event.resource || {};

    const professional =
      appointment.professional ||
      appointment.user;

    const professionalName =
      professional?.name ||
      "Sem profissional";

    const locationName =
      appointment.location?.name ||
      "Sem unidade";

    const clientName =
      appointment.contact?.name ||
      "Cliente";

    const serviceName =
      appointment.service?.name ||
      "Serviço";

    const status =
      appointment.status ||
      "agendado";

    const statusColor =
      statusColors[status] ||
      statusColors.agendado;

    const professionalColor =
      getProfessionalColor(appointment);

    return (
      <div
        className={classes.calendarEvent}
        title={`${clientName} • ${serviceName} • ${professionalName} • ${locationName} • ${
          statusLabels[status] || status
        }`}
      >
        <div className={classes.eventTop}>
          <span className={classes.eventTime}>
            {moment(
              appointment.startTime
            ).format("HH:mm")}
          </span>

          <span className={classes.eventClient}>
            {clientName}
          </span>

          <span
            className={
              classes.eventStatusDot
            }
            style={{
              background: statusColor
            }}
          />
        </div>

        <div className={classes.eventMeta}>
          <span
            className={classes.legendDot}
            style={{
              background:
                professionalColor
            }}
          />

          <span className={classes.eventMetaText}>
            {professionalName}
            {" • "}
            {locationName}
          </span>
        </div>
      </div>
    );
  };


  const CalendarToolbar = toolbar => {
    const navigate = action => toolbar.onNavigate(action);
    const changeView = view => {
      setCalendarView(view);
      toolbar.onView(view);
    };

    return (
      <div className={classes.customToolbar}>
        <div className={classes.toolbarLeft}>
          <button
            className={classes.toolbarTodayBtn}
            onClick={() => navigate("TODAY")}
          >
            <TodayIcon style={{ fontSize: 18 }} />
            Hoje
          </button>

          <button
            className={classes.toolbarIconBtn}
            onClick={() => navigate("PREV")}
            aria-label="Período anterior"
            title="Anterior"
          >
            <ChevronLeftIcon />
          </button>

          <button
            className={classes.toolbarIconBtn}
            onClick={() => navigate("NEXT")}
            aria-label="Próximo período"
            title="Próximo"
          >
            <ChevronRightIcon />
          </button>
        </div>

        <div className={classes.toolbarCenter}>
          {toolbar.label}
        </div>

        <div className={classes.toolbarViews}>
          {[
            ["month", "Mês"],
            ["week", "Semana"],
            ["day", "Dia"],
            ["agenda", "Agenda"]
          ].map(([view, label]) => (
            <button
              key={view}
              className={`${classes.viewBtn} ${
                calendarView === view ? classes.viewBtnActive : ""
              }`}
              onClick={() => changeView(view)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <KoraxPage className={classes.pageRoot}>
      {appointmentModalOpen && (
        <AppointmentModal
          open={appointmentModalOpen}
          onClose={handleCloseAppointmentModal}
          reload={fetchAppointments}
          appointmentId={selectedAppointment?.id}
          initialSlot={selectedSlot}
        />
      )}

      <KoraxPageHeader
        title="Agenda"
        subtitle="Gerencie agendamentos, disponibilidade, serviços e operação da equipe."
      />

      <div className={classes.agendaPulse}>
        <div className={classes.pulseCard}>
          <div className={classes.pulseIcon}>
            <TodayIcon style={{ fontSize: 19 }} />
          </div>
          <div>
            <div className={classes.pulseValue}>
              {appointments.filter(a =>
                moment(a.startTime).isSame(moment(), "day") &&
                a.status !== "cancelado"
              ).length}
            </div>
            <div className={classes.pulseLabel}>
              Agendamentos hoje
            </div>
          </div>
        </div>

        <div className={classes.pulseCard}>
          <div className={classes.pulseIcon}>
            <EventNoteIcon style={{ fontSize: 19 }} />
          </div>
          <div>
            <div className={classes.pulseValue}>
              {appointments.filter(a =>
                moment(a.startTime).isSame(moment(), "day") &&
                a.status === "confirmado"
              ).length}
            </div>
            <div className={classes.pulseLabel}>
              Confirmados
            </div>
          </div>
        </div>

        <div className={classes.pulseCard}>
          <div className={classes.pulseIcon}>
            <AccessTimeIcon style={{ fontSize: 19 }} />
          </div>
          <div>
            <div className={classes.pulseValue}>
              {appointments.filter(a =>
                moment(a.startTime).isSame(moment(), "day") &&
                a.status === "agendado"
              ).length}
            </div>
            <div className={classes.pulseLabel}>
              Aguardando
            </div>
          </div>
        </div>

        <div className={classes.pulseCard}>
          <div className={classes.pulseIcon}>
            <CalendarTodayIcon style={{ fontSize: 18 }} />
          </div>
          <div>
            <div className={classes.pulseValue}>
              {appointments.filter(a =>
                moment(a.startTime).isSame(moment(), "day") &&
                a.status === "em_atendimento"
              ).length}
            </div>
            <div className={classes.pulseLabel}>
              Em atendimento
            </div>
          </div>
        </div>
      </div>

      <Paper className={classes.tabsWrap} elevation={0}>
        <div className={classes.navInner}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Visão geral" />
            <Tab label="Agenda" />
            <Tab label="Serviços" />
            <Tab label="Unidades" />
            <Tab label="Disponibilidade" />
            <Tab label="Bloqueios" />
          </Tabs>

          <Button
            className={classes.navNewButton}
            onClick={handleOpenAppointmentModal}
            startIcon={<AddIcon />}
          >
            Novo agendamento
          </Button>
        </div>
      </Paper>

      {tab === 0 && <AgendaDashboard />}

      {tab === 1 && (
        <> 
        <div className={classes.agendaLayout}>
          <Paper className={classes.calendarCard} elevation={0}>
            <div className={classes.calendarFilters}>
              <div className={classes.filterGroup}>
                <select
                  className={classes.filterSelect}
                  value={locationFilter}
                  onChange={e =>
                    setLocationFilter(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    Todas as unidades
                  </option>

                  {calendarLocations.map(
                    location => (
                      <option
                        key={location.id}
                        value={location.id}
                      >
                        {location.name}
                      </option>
                    )
                  )}
                </select>

                <select
                  className={classes.filterSelect}
                  value={professionalFilter}
                  onChange={e =>
                    setProfessionalFilter(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    Todos os profissionais
                  </option>

                  {calendarProfessionals.map(
                    professional => (
                      <option
                        key={professional.id}
                        value={professional.id}
                      >
                        {professional.name}
                      </option>
                    )
                  )}
                </select>

                <select
                  className={classes.filterSelect}
                  value={statusFilter}
                  onChange={e =>
                    setStatusFilter(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    Todos os status
                  </option>

                  <option value="agendado">
                    Agendado
                  </option>

                  <option value="confirmado">
                    Confirmado
                  </option>

                  <option value="em_atendimento">
                    Em atendimento
                  </option>

                  <option value="finalizado">
                    Finalizado
                  </option>

                  <option value="cancelado">
                    Cancelado
                  </option>

                  <option value="nao_compareceu">
                    Não compareceu
                  </option>
                </select>

                {(locationFilter ||
                  professionalFilter ||
                  statusFilter) && (
                  <button
                    type="button"
                    className={classes.filterClear}
                    onClick={() => {
                      setLocationFilter("");
                      setProfessionalFilter("");
                      setStatusFilter("");
                    }}
                  >
                    Limpar filtros
                  </button>
                )}
              </div>

              <div className={classes.filterCount}>
                {filteredAppointments.length}
                {" "}
                agendamento
                {filteredAppointments.length !== 1
                  ? "s"
                  : ""}
              </div>
            </div>

            <div className={classes.calendarLegend}>
              <div className={classes.legendSide}>
                <span className={classes.legendTitle}>
                  Equipe
                </span>

                {Array.from(
                  new Map(
                    appointments
                      .filter(
                        appointment =>
                          appointment.professionalId ||
                          appointment.professional?.id
                      )
                      .map(appointment => {
                        const id =
                          appointment.professional?.id ||
                          appointment.professionalId;

                        return [
                          String(id),
                          {
                            id,
                            name:
                              appointment.professional?.name ||
                              appointment.user?.name ||
                              `Profissional ${id}`,
                            color:
                              getProfessionalColor(
                                appointment
                              )
                          }
                        ];
                      })
                  ).values()
                ).map(professional => (
                  <span
                    key={professional.id}
                    className={
                      classes.professionalLegendChip
                    }
                  >
                    <span
                      className={classes.legendDot}
                      style={{
                        background:
                          professional.color
                      }}
                    />

                    {professional.name}
                  </span>
                ))}
              </div>

              <div className={classes.legendSide}>
                <span className={classes.legendTitle}>
                  Status
                </span>

                {[
                  ["agendado", "Agendado"],
                  ["confirmado", "Confirmado"],
                  ["em_atendimento", "Em atendimento"]
                ].map(([status, label]) => (
                  <span
                    key={status}
                    className={
                      classes.professionalLegendChip
                    }
                  >
                    <span
                      className={classes.legendDot}
                      style={{
                        background:
                          statusColors[status]
                      }}
                    />
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div className={classes.calendarWrap}>
              <Calendar
                messages={defaultMessages}
                localizer={localizer}
                events={events}
                date={calendarDate}
                view={calendarView}
                views={["month", "week", "day", "agenda"]}
                onNavigate={date => {
                  setCalendarDate(date);
                  if (calendarView !== "month") setSelectedDate(date);
                }}
                onView={view => setCalendarView(view)}
                startAccessor="start"
                endAccessor="end"
                selectable
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventStyleGetter}
                components={{
                  toolbar: CalendarToolbar,
                  event: CalendarEvent
                }}
                dayPropGetter={date => (
                  moment(date).isSame(selectedDate, "day")
                    ? { className: "korax-selected-day" }
                    : {}
                )}
                style={{ height: calendarView === "month" ? 650 : 620 }}
              />
            </div>
          </Paper>

          {calendarView === "month" && (
            <aside className={classes.dayPanel} ref={dayPanelRef}>
              <div className={classes.dayPanelTop}>
                <div>
                  <Typography className={classes.dayPanelTitle}>
                    {moment(selectedDate).format("DD [de] MMMM, dddd")}
                  </Typography>
                  <Typography className={classes.dayPanelSubtitle}>
                    Atendimentos programados para este dia
                  </Typography>
                </div>
                <div className={classes.dayIcon}>
                  <CalendarTodayIcon />
                </div>
              </div>

              <div className={classes.dayContent}>
                <div className={classes.dayStatsGrid}>
                  <div className={classes.dayStat}>
                    <div className={classes.dayStatValue}>
                      {selectedDayAppointments.length}
                    </div>
                    <div className={classes.dayStatLabel}>
                      Atendimentos
                    </div>
                  </div>

                  <div className={classes.dayStat}>
                    <div className={classes.dayStatValue}>
                      {
                        selectedDayAppointments.filter(
                          appointment =>
                            appointment.status === "confirmado"
                        ).length
                      }
                    </div>
                    <div className={classes.dayStatLabel}>
                      Confirmados
                    </div>
                  </div>

                  <div className={classes.dayStat}>
                    <div className={classes.dayStatValue}>
                      {
                        new Set(
                          selectedDayAppointments
                            .map(
                              appointment =>
                                appointment.professionalId ||
                                appointment.professional?.id ||
                                appointment.user?.id
                            )
                            .filter(Boolean)
                        ).size
                      }
                    </div>
                    <div className={classes.dayStatLabel}>
                      Profissionais
                    </div>
                  </div>
                </div>

                <Chip
                  label={
                    selectedDayAppointments.length === 0
                      ? "Sem atendimentos"
                      : `${selectedDayAppointments.length} atendimento${
                          selectedDayAppointments.length !== 1
                            ? "s"
                            : ""
                        }`
                  }
                  size="small"
                  style={{
                    background:
                      selectedDayAppointments.length
                        ? "#EEF5FF"
                        : "#F8FAFC",
                    color:
                      selectedDayAppointments.length
                        ? "#0057FF"
                        : "#64748B",
                    border: "1px solid #E2E8F0",
                    fontWeight: 950,
                    marginBottom: 12
                  }}
                />

                <div className={classes.slotsList}>
                  {selectedDayAppointments.length === 0 ? (
                    <div
                      style={{
                        padding: "28px 16px",
                        textAlign: "center",
                        border: "1px dashed #CBD5E1",
                        borderRadius: 16,
                        background: "#F8FAFC"
                      }}
                    >
                      <CalendarTodayIcon
                        style={{
                          fontSize: 28,
                          color: "#94A3B8",
                          marginBottom: 6
                        }}
                      />

                      <Typography
                        style={{
                          fontWeight: 900,
                          color: "#334155",
                          fontSize: ".85rem"
                        }}
                      >
                        Nenhum atendimento neste dia
                      </Typography>

                      <Typography
                        style={{
                          color: "#94A3B8",
                          fontSize: ".72rem",
                          marginTop: 4
                        }}
                      >
                        Clique no calendário ou crie um novo agendamento.
                      </Typography>
                    </div>
                  ) : (
                    selectedDayAppointments.map(appointment => {
                      const status =
                        appointment.status || "agendado";

                      const statusColor =
                        statusColors[status] ||
                        statusColors.agendado;

                      const professionalColor =
                        getProfessionalColor(
                          appointment
                        );

                      const professionalName =
                        appointment.professional?.name ||
                        appointment.user?.name ||
                        "Profissional";

                      const locationName =
                        appointment.location?.name ||
                        "Sem unidade";

                      return (
                        <div
                          key={appointment.id}
                          className={classes.slotRow}
                          onClick={() =>
                            handleSelectEvent({
                              start: new Date(
                                appointment.startTime
                              ),
                              end: new Date(
                                appointment.endTime
                              ),
                              resource: appointment
                            })
                          }
                          style={{
                            borderLeft:
                              `4px solid ${professionalColor}`,
                            alignItems: "flex-start"
                          }}
                        >
                          <div className={classes.slotClock}>
                            <AccessTimeIcon
                              style={{
                                fontSize: 20
                              }}
                            />
                          </div>

                          <div
                            style={{
                              minWidth: 0,
                              flex: 1
                            }}
                          >
                            <div className={classes.slotTime}>
                              {moment(
                                appointment.startTime
                              ).format("HH:mm")}
                              {" - "}
                              {moment(
                                appointment.endTime
                              ).format("HH:mm")}
                            </div>

                            <div
                              style={{
                                fontWeight: 950,
                                color: "#0F172A",
                                fontSize: ".8rem",
                                marginTop: 3,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {appointment.contact?.name ||
                                "Cliente"}
                            </div>

                            <div className={classes.slotInfo}>
                              {professionalName}
                              {" • "}
                              {locationName}
                            </div>

                            <div
                              className={classes.slotInfo}
                              style={{
                                marginTop: 2
                              }}
                            >
                              {appointment.service?.name ||
                                "Serviço"}
                            </div>
                          </div>

                          <span
                            className={classes.slotPill}
                            style={{
                              background:
                                `${statusColor}14`,
                              color: statusColor,
                              border:
                                `1px solid ${statusColor}33`
                            }}
                          >
                            {statusLabels[status] ||
                              status}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className={classes.aiCard}>
                  <div>
                    <div className={classes.aiTitle}>
                      ✨ Korax IA conectada à agenda
                    </div>

                    <div className={classes.aiText}>
                      A IA consulta serviços, unidades, profissionais e disponibilidade antes de oferecer horários ao cliente.
                    </div>
                  </div>

                  <Chip
                    label="Ativa"
                    size="small"
                    style={{
                      background: "#ECFDF5",
                      color: "#059669",
                      border: "1px solid #A7F3D0",
                      fontWeight: 900
                    }}
                  />
                </div>
              </div>
            </aside>
          )}
        </div>

        </>
      )}

      {tab === 2 && <ServicesManager />}
      {tab === 3 && <LocationsManager />}
      {tab === 4 && <AvailabilityManager />}
      {tab === 5 && <ScheduleBlocksManager />}

      <div className={classes.mobileSpacer} />
    </KoraxPage>
  );
};

export default Agenda;
